import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { GET_PANEL_LLEGADAS_SALIDAS } from '../../graphql/panelControl';
import * as XLSX from 'xlsx';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Home,
  Users,
  Download,
  ChevronLeft,
  ChevronRight,
  Eye,
} from 'lucide-react';
import './LlegadasSalidas.css';

function LlegadasSalidas() {
  const navigate = useNavigate();
  const hoy = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
  const [fecha, setFecha] = useState(hoy);

  const { data, loading } = useQuery(GET_PANEL_LLEGADAS_SALIDAS, {
    variables: { fecha },
    pollInterval: 15000,
  });

  const panel = data?.panelLlegadasSalidas;
  const llegadas = panel?.llegadas || [];
  const salidas = panel?.salidas || [];
  const inHouse = panel?.in_house || [];

  const cambiarFecha = (dias) => {
    const d = new Date(fecha + 'T12:00:00');
    d.setDate(d.getDate() + dias);
    setFecha(d.toLocaleDateString('en-CA'));
  };

  const formatFecha = (f) => {
    if (!f) return '';
    const s = String(f);
    // If it's a clean YYYY-MM-DD, add time to avoid timezone shift
    let d;
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      d = new Date(s + 'T12:00:00');
    } else {
      // Try parsing directly (handles ISO strings, Date.toString() output, etc.)
      d = new Date(s);
    }
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('es-CO', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  };

  const exportarExcel = () => {
    const ws1Data = llegadas.map(l => ({
      'Tipo': l.tipo === 'reserva' ? 'Reserva' : 'Walk-in',
      'Código': l.codigo || '',
      'Huésped': l.huesped_nombre,
      'Documento': l.huesped_documento || '',
      'PAX': l.pax,
      'Habitación': `${l.habitacion_tipo} - ${l.habitacion_numero}`,
      'Entrada': formatFecha(l.fecha_entrada),
      'Salida': formatFecha(l.fecha_salida),
      'Noches': l.noches,
      'Estado': l.estado,
    }));

    const ws2Data = salidas.map(s => ({
      'Código': s.codigo,
      'Huésped': s.huesped_nombre,
      'PAX': s.pax,
      'Habitación': `${s.habitacion_tipo} - ${s.habitacion_numero}`,
      'Entrada': formatFecha(s.fecha_entrada),
      'Salida Prevista': formatFecha(s.fecha_salida_prevista),
      'Noches': s.noches,
      'Estado': s.estado,
    }));

    const ws3Data = inHouse.map(h => ({
      'Código': h.codigo,
      'Huésped': h.huesped_nombre,
      'PAX': h.pax,
      'Habitación': `${h.habitacion_tipo} - ${h.habitacion_numero}`,
      'Entrada': formatFecha(h.fecha_entrada),
      'Salida Prevista': formatFecha(h.fecha_salida_prevista),
      'Noches Totales': h.noches_totales,
      'Noches Restantes': h.noches_restantes,
      'Días Extra (Overstay)': h.dias_overstay || 0,
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(ws1Data.length ? ws1Data : [{ Info: 'Sin llegadas' }]), 'Llegadas');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(ws2Data.length ? ws2Data : [{ Info: 'Sin salidas' }]), 'Salidas');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(ws3Data.length ? ws3Data : [{ Info: 'Sin huéspedes in house' }]), 'In House');
    XLSX.writeFile(wb, `Llegadas_Salidas_${fecha}.xlsx`);
  };

  const ocupPct = panel ? Math.round((panel.ocupacion_actual / Math.max(panel.capacidad_total, 1)) * 100) : 0;

  return (
    <div className="ls">
      {/* Header */}
      <div className="ls__header">
        <div className="ls__header-left">
          <h2 className="ls__title">Llegadas y salidas hoy</h2>
          <p className="ls__subtitle">Controla las entradas y salidas de huéspedes para el día de hoy</p>
        </div>
        <button className="ls__btn-export" onClick={exportarExcel}>
          <Download size={16} /> Exportar a Excel
        </button>
      </div>

      {/* Date nav + gauges */}
      <div className="ls__controls">
        <div className="ls__date-nav">
          <button className="ls__date-btn" onClick={() => cambiarFecha(-1)}><ChevronLeft size={18} /></button>
          <span className="ls__date-badge">{formatFecha(fecha)}</span>
          <button className="ls__date-btn" onClick={() => cambiarFecha(1)}><ChevronRight size={18} /></button>
        </div>
        <div className="ls__gauges">
          <div className="ls__gauge">
            <div className="ls__gauge-circle">
              <svg viewBox="0 0 36 36">
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="var(--hotel-border)"
                  strokeWidth="3"
                />
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="var(--hotel-primary)"
                  strokeWidth="3"
                  strokeDasharray={`${ocupPct}, 100`}
                />
              </svg>
              <span className="ls__gauge-text">{panel?.ocupacion_actual || 0}</span>
            </div>
            <span className="ls__gauge-label">Ocupación</span>
          </div>
          <div className="ls__gauge">
            <div className="ls__gauge-circle">
              <svg viewBox="0 0 36 36">
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="var(--hotel-border)"
                  strokeWidth="3"
                />
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="#22c55e"
                  strokeWidth="3"
                  strokeDasharray={`${panel ? Math.round(((panel.capacidad_total - panel.ocupacion_actual) / Math.max(panel.capacidad_total, 1)) * 100) : 0}, 100`}
                />
              </svg>
              <span className="ls__gauge-text">{panel ? panel.capacidad_total - panel.ocupacion_actual : 0}</span>
            </div>
            <span className="ls__gauge-label">Capacidad</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="ls__loading">Cargando datos...</div>
      ) : (
        <div className="ls__columns">
          {/* Llegadas */}
          <div className="ls__column">
            <h3 className="ls__col-title">
              <ArrowDownCircle size={18} className="ls__col-icon ls__col-icon--llegada" />
              Llegadas
              <span className="ls__col-badge ls__col-badge--llegada">{llegadas.length}</span>
            </h3>
            {llegadas.length === 0 ? (
              <div className="ls__col-empty">Sin llegadas programadas</div>
            ) : (
              llegadas.map(l => (
                <div key={`${l.tipo}-${l.id}`} className={`ls__card ${l.estado === 'checked_in' ? 'ls__card--checked-in' : ''}`}>
                  <div className="ls__card-header">
                    <span className="ls__card-nombre">{l.huesped_nombre}</span>
                    <span className="ls__card-pax"><Users size={12} /> {l.pax} Pax</span>
                  </div>
                  <div className="ls__card-room">
                    {l.habitacion_tipo} - {l.habitacion_numero}
                  </div>
                  <div className="ls__card-dates">
                    Fechas: {formatFecha(l.fecha_entrada)} - {formatFecha(l.fecha_salida)} ({l.noches}n)
                  </div>
                  {l.estado === 'checked_in' && (
                    <div className="ls__card-checkin-info">
                      Check in realizado
                    </div>
                  )}
                  <div className="ls__card-actions">
                    {l.estado !== 'checked_in' && (
                      <>
                        <button className="ls__card-btn ls__card-btn--checkin" onClick={() => navigate('/hospedajes/checkin')}>Check-in</button>
                        <button className="ls__card-btn ls__card-btn--noshow">No show</button>
                      </>
                    )}
                    {l.estado === 'checked_in' && (
                      <button className="ls__card-btn ls__card-btn--deshacer">Deshacer check-in</button>
                    )}
                    <button className="ls__card-btn ls__card-btn--detalles" onClick={() => navigate(l.reserva_id ? `/reservas` : '/hospedajes')}>
                      Detalles
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Salidas */}
          <div className="ls__column">
            <h3 className="ls__col-title">
              <ArrowUpCircle size={18} className="ls__col-icon ls__col-icon--salida" />
              Salidas
              <span className="ls__col-badge ls__col-badge--salida">{salidas.length}</span>
            </h3>
            {salidas.length === 0 ? (
              <div className="ls__col-empty">Sin salidas programadas</div>
            ) : (
              salidas.map(s => (
                <div key={s.id} className="ls__card">
                  <div className="ls__card-header">
                    <span className="ls__card-nombre">{s.huesped_nombre}</span>
                    <span className="ls__card-pax"><Users size={12} /> {s.pax} Pax</span>
                  </div>
                  <div className="ls__card-room">
                    {s.habitacion_tipo} - {s.habitacion_numero}
                  </div>
                  <div className="ls__card-dates">
                    Fechas: {formatFecha(s.fecha_entrada)} - {formatFecha(s.fecha_salida_prevista)} ({s.noches}n)
                  </div>
                  <div className="ls__card-actions">
                    {s.estado === 'activo' && (
                      <button className="ls__card-btn ls__card-btn--checkout" onClick={() => navigate('/hospedajes')}>Check-out</button>
                    )}
                    <button className="ls__card-btn ls__card-btn--detalles" onClick={() => navigate('/hospedajes')}>
                      Detalles
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* In House */}
          <div className="ls__column">
            <h3 className="ls__col-title">
              <Home size={18} className="ls__col-icon ls__col-icon--inhouse" />
              In house
              <span className="ls__col-badge ls__col-badge--inhouse">{inHouse.length}</span>
            </h3>
            {inHouse.length === 0 ? (
              <div className="ls__col-empty">Sin huéspedes in house</div>
            ) : (
              inHouse.map(h => (
                <div key={h.id} className="ls__card">
                  <div className="ls__card-header">
                    <span className="ls__card-nombre">{h.huesped_nombre}</span>
                    <span className="ls__card-pax"><Users size={12} /> {h.pax} Pax</span>
                  </div>
                  <div className="ls__card-room">
                    {h.habitacion_tipo} - {h.habitacion_numero}
                  </div>
                  <div className="ls__card-dates">
                    Fechas: {formatFecha(h.fecha_entrada)} - {formatFecha(h.fecha_salida_prevista)} ({h.noches_totales}n)
                  </div>
                  {h.dias_overstay > 0 ? (
                    <div className="ls__card-warning ls__card-warning--overstay">
                      Overstay: {h.dias_overstay} {h.dias_overstay === 1 ? 'día' : 'días'} extra
                    </div>
                  ) : h.noches_restantes <= 1 && (
                    <div className="ls__card-warning">Sale {h.noches_restantes === 0 ? 'hoy' : 'mañana'}</div>
                  )}
                  <div className="ls__card-actions">
                    <button className="ls__card-btn ls__card-btn--detalles" onClick={() => navigate('/hospedajes')}>
                      Detalles
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default LlegadasSalidas;
